import { useState, useCallback, useMemo } from "react";
import {
  Box,
  Button,
  CircularProgress,
  IconButton,
  TableCell,
  TableRow,
  Typography,
} from "@mui/material";
import {
  CheckCircleOutline,
  KeyboardArrowDown,
  KeyboardArrowRight,
  SaveOutlined,
} from "@mui/icons-material";
import { VolumeTypeEnum } from "../../../../../store/types/secureVolume.types";
import type { SecureVolumeContainer } from "../../../../../store/types/secureVolume.types";
import {
  getInputValue,
  getYearlyTypeMap,
  getSecuredPercentage,
  computeFacilityTotal,
  distributeTotalByHistory,
  getQuarterForecast,
  percentFromKwh,
} from "../helpers";
import { fmt } from "../types";
import { VolumeCellContent } from "./VolumeCellContent";
import { InputQuarterCell } from "./InputQuarterCell";
import { TotalEditCell } from "./TotalEditCell";
import { TotalDisplay } from "./TotalDisplay";

interface FacilityRowProps {
  facility: SecureVolumeContainer;
  index: number;
  years: number[];
  inputYear: number;
  inputValues: Record<string, string>;
  onInputChange: (updates: Record<string, string>) => void;
  onSave?: (facilityIndex: number, quarters: Record<number, number>) => void;
  isSaving?: boolean;
}

export function FacilityRow({
  facility,
  index,
  years,
  inputYear,
  inputValues,
  onInputChange,
  onSave,
  isSaving,
}: FacilityRowProps) {
  const [open, setOpen] = useState(false);
  const keyPrefix = `f${index}`;

  // Check if any quarter for this facility has been modified
  const hasChanges = useMemo(() => {
    for (let q = 1; q <= 4; q++) {
      const key = `${keyPrefix}-${inputYear}-${q}`;
      if (key in inputValues && inputValues[key] !== "") return true;
    }
    return false;
  }, [keyPrefix, inputYear, inputValues]);

  // Target year already has Secured volumes → read-only, no input
  const isTargetSecured = useMemo(
    () =>
      facility.QuarterVolumes.some(
        (v) =>
          v.Year === inputYear &&
          v.VolumeType === VolumeTypeEnum.Secured &&
          v.Quantity > 0,
      ),
    [facility.QuarterVolumes, inputYear],
  );

  const isEditable = !isTargetSecured;

  // Compact mode: saved InProgress data exists and no pending edits
  const hasSavedData = useMemo(
    () =>
      facility.QuarterVolumes.some(
        (v) =>
          v.Year === inputYear &&
          v.VolumeType === VolumeTypeEnum.InProgress,
      ),
    [facility.QuarterVolumes, inputYear],
  );
  const isCompact = hasSavedData && !hasChanges && isEditable;

  const handleSave = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      const quarters: Record<number, number> = {};
      for (let q = 1; q <= 4; q++) {
        const val = getInputValue(keyPrefix, inputYear, q, facility.QuarterVolumes, inputValues);
        quarters[q] = Number(val) || 0;
      }
      onSave?.(index, quarters);
      setOpen(false);
    },
    [keyPrefix, inputYear, facility.QuarterVolumes, inputValues, index, onSave],
  );

  const handleDistribute = useCallback(
    (total: number) => {
      const distributed = distributeTotalByHistory(
        total,
        facility.QuarterVolumes,
        inputYear,
      );
      const updates: Record<string, string> = {};
      for (let q = 1; q <= 4; q++) {
        const kwh = distributed[q];
        const forecast = getQuarterForecast(facility.QuarterVolumes, inputYear, q);
        updates[`${keyPrefix}-${inputYear}-${q}`] = kwh.toString();
        updates[`${keyPrefix}-${inputYear}-${q}-pct`] = forecast > 0
          ? percentFromKwh(kwh, forecast).toFixed(1)
          : "";
      }
      onInputChange(updates);
    },
    [keyPrefix, inputYear, facility.QuarterVolumes, onInputChange],
  );

  return (
    <>
      {/* Facility header row */}
      <TableRow
        sx={{
          cursor: "pointer",
          "&:hover": { bgcolor: "action.hover" },
          borderTop: index > 0 ? 2 : 0,
          borderColor: "divider",
        }}
        onClick={() => setOpen(!open)}
      >
        <TableCell
          colSpan={years.length + 1}
          sx={{
            py: 0.75,
            bgcolor: "grey.200",
            borderBottom: "1px solid",
            borderColor: "divider",
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              width: "100%",
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center" }}>
              <IconButton size="small" sx={{ mr: 1 }}>
                {open ? <KeyboardArrowDown /> : <KeyboardArrowRight />}
              </IconButton>
              <Typography variant="body2" fontWeight={600}>
                {facility.Address ||
                  `Anläggning ${facility.BillingPointId}`}
              </Typography>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ ml: 1 }}
              >
                Anl.nr: {facility.BillingPointId}
              </Typography>
            </Box>
            {isEditable && (hasChanges || isSaving) && (
              <Button
                size="small"
                variant="contained"
                disabled={isSaving}
                startIcon={
                  isSaving ? (
                    <CircularProgress size={16} color="inherit" />
                  ) : (
                    <SaveOutlined sx={{ fontSize: 16 }} />
                  )
                }
                onClick={handleSave}
                sx={{ ml: 2, textTransform: "none", py: 0.25, px: 1.5 }}
              >
                {isSaving ? "Sparar..." : "Spara"}
              </Button>
            )}
            {isCompact && !open && (
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, ml: "auto" }}>
                <CheckCircleOutline sx={{ fontSize: 16, color: "success.main" }} />
                <Typography variant="body2" fontWeight={600}>
                  {fmt.format(
                    computeFacilityTotal(
                      keyPrefix,
                      inputYear,
                      facility.QuarterVolumes,
                      inputValues,
                    ),
                  )}{" "}
                  kWh
                </Typography>
              </Box>
            )}
          </Box>
        </TableCell>
      </TableRow>

      {/* Summary totals row – hidden when compact and collapsed */}
      {(!isCompact || open) && (
        <TableRow sx={{ bgcolor: "grey.100", "& > *": { borderBottom: "unset" } }}>
          <TableCell sx={{ py: 0.5 }}>
            <Typography variant="caption" color="text.secondary">
              Totalt
            </Typography>
          </TableCell>
          {years.map((year) => {
            if (isEditable && year === inputYear) {
              const currentTotal = computeFacilityTotal(
                keyPrefix,
                year,
                facility.QuarterVolumes,
                inputValues,
              );
              const securedPct = getSecuredPercentage(
                facility.QuarterVolumes,
                year,
                inputValues,
                keyPrefix,
              );
              return (
                <TableCell
                  key={year}
                  align="right"
                  sx={{
                    py: 0.5,
                    borderLeft: "1px solid",
                    borderLeftColor: "divider",
                  }}
                >
                  <TotalEditCell
                    currentTotal={currentTotal}
                    securedPct={securedPct}
                    onDistribute={handleDistribute}
                  />
                </TableCell>
              );
            }
            return (
              <TableCell
                key={year}
                align="right"
                sx={{
                  py: 0.5,
                  borderLeft: "1px solid",
                  borderLeftColor: "divider",
                }}
              >
                <TotalDisplay
                  typeMap={getYearlyTypeMap(facility.QuarterVolumes, year)}
                  securedPct={getSecuredPercentage(
                    facility.QuarterVolumes,
                    year,
                  )}
                />
              </TableCell>
            );
          })}
        </TableRow>
      )}

      {/* Expanded quarter detail – rows in the same table so columns line up with the header */}
      {open &&
        [1, 2, 3, 4].map((quarter) => (
          <TableRow
            key={quarter}
            sx={{
              bgcolor: quarter % 2 === 0 ? "grey.100" : "transparent",
              "& > *": { borderBottom: quarter === 4 ? "1px solid" : "unset", borderColor: "divider" },
            }}
          >
            <TableCell sx={{ py: 0.5, pl: 2 }}>
              <Typography variant="caption" color="text.secondary">
                Q{quarter}
              </Typography>
            </TableCell>
            {years.map((year) => (
              <TableCell
                key={year}
                align="right"
                sx={{
                  py: 0.5,
                  borderLeft: "1px solid",
                  borderLeftColor: "divider",
                }}
              >
                <VolumeCellContent
                  volumes={facility.QuarterVolumes}
                  year={year}
                  quarter={quarter}
                  excludeTypes={
                    isEditable && year === inputYear
                      ? [VolumeTypeEnum.InProgress, VolumeTypeEnum.Secured, VolumeTypeEnum.Forecast]
                      : []
                  }
                />
                {isEditable && year === inputYear && (
                  <InputQuarterCell
                    keyPrefix={keyPrefix}
                    year={year}
                    quarter={quarter}
                    volumes={facility.QuarterVolumes}
                    inputValues={inputValues}
                    onInputChange={onInputChange}
                  />
                )}
              </TableCell>
            ))}
          </TableRow>
        ))}
    </>
  );
}
